import { FC, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Button, Input, ModalBody, ModalFooter } from 'reactstrap';

import Scrollable from 'views/Widgets/Scrollable';
import { ColumnMapping, OwnedColumn } from './types';

interface ColumnMappingStepProps {
  headers: string[];
  dataRows: string[][];
  columnMappings: ColumnMapping[];
  ownedColumns: OwnedColumn[];
  availableIdentifiers: string[];
  mappingValidation: { valid: boolean; error: string };
  pageSize: number;
  onMappingChange: (colIndex: number, value: string) => void;
  getMappingValue: (mapping: ColumnMapping) => string;
  onBack: () => void;
  onNext: () => void;
}

const ColumnMappingStep: FC<ColumnMappingStepProps> = ({
  headers,
  dataRows,
  columnMappings,
  ownedColumns,
  availableIdentifiers,
  mappingValidation,
  pageSize,
  onMappingChange,
  getMappingValue,
  onBack,
  onNext,
}) => {
  const { formatMessage } = useIntl();
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(dataRows.length / pageSize));
  const paginatedRows = useMemo(
    () => dataRows.slice(page * pageSize, (page + 1) * pageSize),
    [dataRows, page, pageSize]
  );

  return (
    <>
      <ModalBody>
        <p>
          <FormattedMessage
            id="app.custom_campaign_data.column_mapping.description"
            defaultMessage="Map each CSV column to an existing dataset column, create a new column, or ignore it."
          />
        </p>
        {!mappingValidation.valid && mappingValidation.error && (
          <Alert color="warning">{mappingValidation.error}</Alert>
        )}
        <Scrollable>
          <div className="table-responsive">
            <table className="card-table table table-sm mb-0 small">
              <thead>
                <tr>
                  {headers.map((header, colIdx) => (
                    <th key={colIdx} style={{ minWidth: 150 }}>
                      <div className="text-muted">{header}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="list">
                <tr>
                  {headers.map((_, colIdx) => {
                    const mapping = columnMappings[colIdx];
                    if (!mapping) return <td key={colIdx} />;
                    return (
                      <td key={colIdx} style={{ minWidth: 150 }}>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={getMappingValue(mapping)}
                          onChange={(e) =>
                            onMappingChange(colIdx, e.target.value)
                          }
                        >
                          {availableIdentifiers.length > 0 && (
                            <optgroup
                              label={formatMessage({
                                id: 'app.custom_campaign_data.column_mapping.identifier_group',
                                defaultMessage: 'Identifier',
                              })}
                            >
                              {availableIdentifiers.includes('email') && (
                                <option value="__email__">
                                  {formatMessage({
                                    id: 'app.custom_campaign_data.column_mapping.email',
                                    defaultMessage: 'Email (identifier)',
                                  })}
                                </option>
                              )}
                              {availableIdentifiers.includes('external_id') && (
                                <option value="__external_id__">
                                  {formatMessage({
                                    id: 'app.custom_campaign_data.column_mapping.external_id',
                                    defaultMessage: 'External ID (identifier)',
                                  })}
                                </option>
                              )}
                            </optgroup>
                          )}
                          {ownedColumns.length > 0 && (
                            <optgroup
                              label={formatMessage({
                                id: 'app.custom_campaign_data.column_mapping.existing_group',
                                defaultMessage: 'Existing columns',
                              })}
                            >
                              {ownedColumns.map((ec) => (
                                <option key={ec.key} value={ec.key}>
                                  {ec.label}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup
                            label={formatMessage({
                              id: 'app.custom_campaign_data.column_mapping.other_group',
                              defaultMessage: 'Other',
                            })}
                          >
                            <option value="__new__">
                              {formatMessage({
                                id: 'app.custom_campaign_data.column_mapping.new_column',
                                defaultMessage: 'New column',
                              })}
                            </option>
                            <option value="__ignore__">
                              {formatMessage({
                                id: 'app.custom_campaign_data.column_mapping.ignore',
                                defaultMessage: 'Ignore',
                              })}
                            </option>
                          </optgroup>
                        </Input>
                      </td>
                    );
                  })}
                </tr>
                {paginatedRows.map((row, rowIdx) => (
                  <tr key={rowIdx} className="text-muted">
                    {row.map((cell, colIdx) => (
                      <td key={colIdx}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Scrollable>
        {totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between mt-2">
            <Button
              size="sm"
              color="light"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              <FormattedMessage
                id="app.custom_campaign_data.pagination.previous"
                defaultMessage="Previous"
              />
            </Button>
            <span className="text-muted small">
              <FormattedMessage
                id="app.custom_campaign_data.pagination.info"
                defaultMessage="{page} of {total}"
                values={{ page: page + 1, total: totalPages }}
              />
            </span>
            <Button
              size="sm"
              color="light"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <FormattedMessage
                id="app.custom_campaign_data.pagination.next"
                defaultMessage="Next"
              />
            </Button>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onBack}>
          <FormattedMessage
            id="app.custom_campaign_data.back"
            defaultMessage="Back"
          />
        </Button>
        <Button
          color="primary"
          disabled={!mappingValidation.valid}
          onClick={onNext}
        >
          <FormattedMessage
            id="app.custom_campaign_data.next"
            defaultMessage="Next"
          />
        </Button>
      </ModalFooter>
    </>
  );
};

export default ColumnMappingStep;
